import { ValueOf } from 'type-fest'
import type { ContentChipColor } from 'ComponentLibrary/src/piazza/content/ContentChip/ContentChip'

export function isParkingType(type: TenantUsageTypeLabelKeys) {
    return ['parking', 'outdoor_parking', 'underground_parking', 'garage_parking', 'other_parking'].includes(type)
}

export type TenantUsageTypeLabelKeys = keyof typeof TENANT_USAGE_TYPE_LABELS
export type TenantRentalStatusLabelKeys = keyof typeof TENANT_RENTAL_STATUS_LABELS
export type TenantRentalAgreementTypeLabelKeys = keyof typeof TENANT_RENTAL_AGREEMENT_TYPE_LABELS

export const TENANT_USAGE_TYPE_LABELS = {
    residential: 'Wohnen',
    commercial: 'Gewerbe',
    gastronomy: 'Gastronomie',
    industry: 'Industrie, Produktion',
    hotel: 'Beherbergung',
    storage: 'Lager',
    underground_parking: 'Tiefgaragenstellplatz',
    leisure: 'Freizeit',
    social: 'Soziale Nutzung',
    archive: 'Keller, Archiv',
    outdoor_parking: 'Außenstellplatz',
    parking: 'Parken',
    retail: 'Handel',
    logistics: 'Logistik',
    office: 'Büro',
    other: 'Andere',
    total: 'Total',
    other_parking: 'Sonstiger Stellplatz',
    open_space: 'Freifläche',
    other_use: 'Sonstige Nutzung',
    garage_parking: 'Garagenstellplatz',
}

export const TENANT_RENTAL_STATUS_LABELS = {
    rented: 'Vermietet',
    unrented: 'Leerstand',
    self_used: 'Eigengenutzt',
}

export const TENANT_RENTAL_AGREEMENT_TYPE_LABELS = {
    fixed: 'Frei',
    indexed: 'Index',
    graduated: 'Staffel',
    unknown: 'Unbekannt',
}

export type TenantUsageTypeOptionKeys = (typeof tenantUsageTypeOptions)[0]['value']

export type TenantRentalStatusOptionKeys = (typeof tenantRentalStatusOptions)[0]['value']

type RentalStatusOption = {
    value: keyof typeof TENANT_RENTAL_STATUS_LABELS
    label: ValueOf<typeof TENANT_RENTAL_STATUS_LABELS>
}

type RentalAgreementTypeOption = {
    value: keyof typeof TENANT_RENTAL_AGREEMENT_TYPE_LABELS
    label: ValueOf<typeof TENANT_RENTAL_AGREEMENT_TYPE_LABELS>
}

export const tenantUsageTypeOptions = [
    { value: 'residential' as const, label: TENANT_USAGE_TYPE_LABELS.residential },
    { value: 'office' as const, label: TENANT_USAGE_TYPE_LABELS.office },
    { value: 'retail' as const, label: TENANT_USAGE_TYPE_LABELS.retail },
    { value: 'gastronomy' as const, label: TENANT_USAGE_TYPE_LABELS.gastronomy },
    { value: 'logistics' as const, label: TENANT_USAGE_TYPE_LABELS.logistics },
    { value: 'industry' as const, label: TENANT_USAGE_TYPE_LABELS.industry },
    { value: 'hotel' as const, label: TENANT_USAGE_TYPE_LABELS.hotel },
    { value: 'storage' as const, label: TENANT_USAGE_TYPE_LABELS.storage },
    { value: 'commercial' as const, label: TENANT_USAGE_TYPE_LABELS.commercial },
    { value: 'social' as const, label: TENANT_USAGE_TYPE_LABELS.social },
    { value: 'leisure' as const, label: TENANT_USAGE_TYPE_LABELS.leisure },
    { value: 'archive' as const, label: TENANT_USAGE_TYPE_LABELS.archive },
    { value: 'outdoor_parking' as const, label: TENANT_USAGE_TYPE_LABELS.outdoor_parking },
    { value: 'garage_parking' as const, label: TENANT_USAGE_TYPE_LABELS.garage_parking },
    { value: 'underground_parking' as const, label: TENANT_USAGE_TYPE_LABELS.underground_parking },
    { value: 'other_parking' as const, label: TENANT_USAGE_TYPE_LABELS.other_parking },
    { value: 'other_use' as const, label: TENANT_USAGE_TYPE_LABELS.other_use },
    { value: 'open_space' as const, label: TENANT_USAGE_TYPE_LABELS.open_space },
    { value: 'parking' as const, label: TENANT_USAGE_TYPE_LABELS.parking },
    { value: 'other' as const, label: TENANT_USAGE_TYPE_LABELS.other },
]

export const tenantRentalStatusOptions: RentalStatusOption[] = [
    { value: 'rented', label: TENANT_RENTAL_STATUS_LABELS.rented },
    { value: 'unrented', label: TENANT_RENTAL_STATUS_LABELS.unrented },
    { value: 'self_used', label: TENANT_RENTAL_STATUS_LABELS.self_used },
]

export const rentalAgreementTypeOptions: RentalAgreementTypeOption[] = [
    { value: 'fixed', label: TENANT_RENTAL_AGREEMENT_TYPE_LABELS.fixed },
    { value: 'indexed', label: TENANT_RENTAL_AGREEMENT_TYPE_LABELS.indexed },
    { value: 'graduated', label: TENANT_RENTAL_AGREEMENT_TYPE_LABELS.graduated },
    { value: 'unknown', label: TENANT_RENTAL_AGREEMENT_TYPE_LABELS.unknown },
]

export const getTenantRentalStatusColor = (rentalStatus: TenantRentalStatusLabelKeys): ContentChipColor => {
    switch (rentalStatus) {
        case 'rented':
            return 'green'
        case 'unrented':
            return 'red'
        case 'self_used':
            return 'lime'
        default:
            return 'grey'
    }
}

export const getTenantUsageTypeColor = (usageType: TenantUsageTypeLabelKeys): ContentChipColor => {
    switch (usageType) {
        case 'retail':
        case 'commercial':
            return 'green'
        case 'gastronomy':
        case 'hotel':
            return 'orange'
        case 'office':
            return 'blue'
        case 'residential':
            return 'royal'
        case 'storage':
        case 'archive':
            return 'lime'
        case 'industry':
        case 'logistics':
            return 'turquoise'
        case 'parking':
        case 'outdoor_parking':
        case 'underground_parking':
            return 'leaf'
        case 'leisure':
            return 'purple'
        case 'social':
            return 'grape'
        case 'other':
        case 'total':
        case 'open_space':
            return 'grey'
        case 'other_parking':
        case 'garage_parking':
        case 'other_use':
            return 'grey'
        default:
            return 'grey'
    }
}
